import { Injectable } from '@angular/core'
import { selectors } from '@employer/app/store/selectors'
import { environment } from '@employer/environments/environment'
import { RestApiClient } from '@engineering11/web-api-rest'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from 'rxjs'
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators'
import { DEFAULT_NETWORK_MONITORING_CONFIG, getBaseSuffix, TokenStorage } from 'shared-lib'
import { canSend, ConversationStatus, IAssistantConversation, IAssistantMessage, IAssistantRunStep } from '../models/assistant.model'
import { AssistantConversationRepository } from '../repositories/assistant-conversation.repository'
import { AssistantConversationStepsRepository } from '../repositories/assistant-conversation-steps.repository'
import { AssistantMessageRepository } from '../repositories/assistant-message.repository'
import { PageContextService } from './page-context.service'

// Send-message route under `environment.services.assistant` (URI-versioned, id in path).
// Reconciled 2026-08-12 against service-assistant: POST v1/conversations/:conversationId/messages, body { text }.
const sendMessagePath = (conversationId: string) => `conversations/${conversationId}/messages`

/**
 * Raw `RestApiClient` errors are `E11ApiError` (an AxiosError whose `response.data` carries a
 * `statusCode`). `isE11ApiError` is not re-exported from `@engineering11/web-api-rest`'s public entry
 * (its `exports` map only exposes `.`), so we replicate its narrow inline rather than deep-import.
 * A 409 is the busy/conflict signal; we key on the status alone (body `statusCode` OR the HTTP
 * `response.status`) so a 409 whose problem+json body omits other members is still swallowed (CAP-3).
 */
function isBusyConflict(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { statusCode?: number } } })?.response
  return response?.data?.statusCode === 409 || response?.status === 409
}

/**
 * Composes the live-listener repository (CAP-2) into the observables the panel renders, and owns the
 * send seam (CAP-3). A send that races a busy backend (HTTP 409) is swallowed and never surfaced;
 * any other error propagates for the composer's inline, non-blocking error.
 */
@Injectable({ providedIn: 'root' })
export class AssistantConversationService {
  private restClient: RestApiClient
  private conversationId$ = new BehaviorSubject<string | undefined>(undefined)

  /**
   * The active conversation id, exposed so the story-4.8 pending-action listener + confirm correlate to
   * the same conversation this panel is pointed at. Not a backend-shaped value — derived from the id set
   * via `setConversationId`.
   */
  readonly activeConversationId$: Observable<string | undefined> = this.conversationId$.pipe(distinctUntilChanged())

  /** Synchronous read of the active conversation id (used to build the confirm request body). */
  get activeConversationId(): string | undefined {
    return this.conversationId$.value
  }

  /** The live conversation document (or undefined when no conversation is selected). */
  conversation$: Observable<IAssistantConversation | undefined> = this.conversationId$.pipe(
    distinctUntilChanged(),
    // A listener error (auth session expiry, permission-denied, datastore not-found — the SDK
    // boundary this story owns) degrades to an empty conversation rather than killing every derived
    // stream and leaving the panel permanently broken.
    switchMap(id => (id ? this.repository.liveConversation(id).pipe(catchError(() => of(undefined))) : of(undefined))),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  // The caller's FIREBASE auth uid (IUser.authId / token user_id) — NOT getCurrentUserId (appUserId) —
  // and customerKey. Both key the owner-scoped listeners so the firestore owner rule allows the read.
  private authUid$: Observable<string | undefined> = this.store.select(selectors.getCurrentUser).pipe(
    map(user => user?.authId ?? undefined),
    distinctUntilChanged()
  )
  private customerKey$: Observable<string | undefined> = this.store.select(selectors.getCustomerKey$).pipe(distinctUntilChanged())

  // Run replies live in the top-level `assistant_messages` collection, not on the conversation doc — a
  // live collection query keyed to (conversation, uid, customerKey). `startWith([])` resets to empty the
  // instant the conversation switches (shareReplay would otherwise replay the prior conversation's
  // messages until the new listener first emits); a listener error degrades to [].
  messages$: Observable<IAssistantMessage[]> = combineLatest([this.activeConversationId$, this.authUid$, this.customerKey$]).pipe(
    switchMap(([id, uid, ck]) =>
      id && uid && ck
        ? this.messageRepo.liveMessages(uid, ck, id).pipe(
            catchError(() => of([])),
            startWith([])
          )
        : of([])
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  // Run steps (including `client_tool_call` steps the executor dispatches) live in the per-conversation
  // `steps` subcollection, not on the conversation doc — a live subcollection listener keyed the same way.
  steps$: Observable<IAssistantRunStep[]> = combineLatest([this.activeConversationId$, this.authUid$, this.customerKey$]).pipe(
    switchMap(([id, uid, ck]) =>
      id && uid && ck
        ? this.stepsRepo.liveSteps(id, uid, ck).pipe(
            catchError(() => of([])),
            startWith([])
          )
        : of([])
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  status$: Observable<ConversationStatus> = this.conversation$.pipe(map(c => c?.status ?? ConversationStatus.Idle))

  /** True only while a run is actively in progress — drives the "assistant is working" affordance (not shown on Idle). */
  working$: Observable<boolean> = this.status$.pipe(
    map(s => s === ConversationStatus.Running || s === ConversationStatus.AwaitingConfirmation || s === ConversationStatus.AwaitingClient)
  )

  /**
   * Send is permitted only when a conversation is selected AND its status is idle. Requiring a
   * conversation id keeps an unselected/undefined conversation (which defaults `status$` to Idle)
   * from appearing sendable and POSTing a `conversationId: undefined` body (CAP-3).
   */
  canSend$: Observable<boolean> = combineLatest([this.status$, this.conversationId$.pipe(map(id => !!id))]).pipe(
    map(([status, hasConversation]) => hasConversation && canSend(status))
  )

  constructor(
    private repository: AssistantConversationRepository,
    private messageRepo: AssistantMessageRepository,
    private stepsRepo: AssistantConversationStepsRepository,
    private store: Store,
    private pageContext: PageContextService,
    tokenStorage: TokenStorage
  ) {
    this.restClient = new RestApiClient({
      // Reconciled 2026-08-12: the assistant is its own service (service-assistant at /assistant), not jobs-assistant.
      baseUrl: environment.services.assistant,
      token: () => tokenStorage.getAccessToken(),
      baseSuffix: getBaseSuffix(environment, 'assistant'),
      ...DEFAULT_NETWORK_MONITORING_CONFIG,
    })
  }

  /** Point the panel at a conversation; live observables re-target to its document. */
  setConversationId(id: string | undefined): void {
    this.conversationId$.next(id)
  }

  /** Guards against a create-per-open race while the first POST is in flight. */
  private ensuring = false

  /**
   * Ensure the panel has a conversation to attach to. Creates one via POST v1/conversations (returning
   * its id) only when none is selected, then points the panel at it — this is the production path the
   * story-4.2 `setConversationId` seam was left waiting for. Idempotent: a session reuses one conversation,
   * and a create error is swallowed so the panel simply stays in its empty state rather than crashing.
   */
  ensureConversation(): void {
    if (this.conversationId$.value || this.ensuring) return
    this.ensuring = true
    this.restClient
      .post<{ id?: string }>('conversations', {})
      .pipe(
        map(res => res.data?.id),
        catchError(() => of(undefined))
      )
      .subscribe(id => {
        this.ensuring = false
        if (id) this.setConversationId(id)
      })
  }

  /**
   * Send a chat message. On HTTP 409 (conflict/busy) the error is swallowed and the stream completes
   * silently — the client-side send-disable is the primary guard, so a raced 409 is never user-visible
   * (CAP-3). Any other error propagates so the composer can show an inline error and re-enable.
   */
  sendMessage(text: string): Observable<void> {
    const conversationId = this.conversationId$.value
    // No conversation selected → nothing to send to. No-op rather than POST an undefined id.
    if (!conversationId) {
      return EMPTY
    }
    // Reconciled 2026-08-12: conversationId is a path param; body field is `content` (SendMessageDTO).
    // Page-context ids (resolved from live route state + registered providers) let the assistant resolve
    // deictic references like "this job"/"this candidate" to the entity the user is viewing; the server
    // validates each id and permission-checks the read before using it (never trusts it as free text).
    // Recomputed per send, so no id from a prior screen leaks; absent ids are omitted.
    return this.restClient.post<void>(sendMessagePath(conversationId), { content: text, ...this.pageContext.resolve() }).pipe(
      map(() => undefined),
      catchError(error => {
        if (isBusyConflict(error)) {
          return EMPTY
        }
        throw error
      })
    )
  }
}
